import { useMemo, useState } from 'react';
import { computeBid } from '@lbhl/calc';
import {
  BidValidationError,
  formatCents,
  type Adjustment,
  type BidInput,
  type BidResult,
  type EngineeringCount,
  type Measurement,
  type PricingConfig,
  type SurfaceCode,
} from '@lbhl/shared';
import type { SessionUser } from '../lib/api.js';
import { engineeringItems, parseSegments, sumOf, tiersOf } from '../lib/tiers.js';
import { Measurements } from './Measurements.jsx';
import { Engineering, type EngineeringCounts } from './Engineering.jsx';
import { Adjustments, type AdjustRow } from './Adjustments.jsx';
import { Quotes, TotalBarFigures } from './Quotes.jsx';
import { newSegment, type Segment } from './SegmentRows.jsx';

export interface BidMeta {
  customer: { name: string; email: string | null; phone: string | null };
  property: { addressLine1: string; city: string | null; region: string | null; postalCode: string | null };
}

interface Props {
  pricing: PricingConfig;
  user: SessionUser;
  onSave(input: BidInput, result: BidResult, meta: BidMeta): Promise<void>;
}

/** Every surface starts with one empty box to type into. */
function initialSegments(pricing: PricingConfig): Record<string, Segment[]> {
  const out: Record<string, Segment[]> = {};
  for (const s of pricing.surfaces) out[s.code] = [newSegment()];
  return out;
}

/**
 * Builds a bid that quotes every package.
 *
 * There is no package to choose: the estimator measures once, and each tier is
 * priced on the surfaces it includes. What used to be a radio group at the top of
 * this form is now the shape of the Measurements section itself.
 */
export function BidForm({ pricing, user, onSave }: Props) {
  const tiers = useMemo(() => tiersOf(pricing), [pricing]);
  const engItems = useMemo(() => engineeringItems(pricing), [pricing]);

  const [colorCode, setColorCode] = useState(pricing.colorOptions[0]?.code ?? '');
  const [clipColorCode, setClipColorCode] = useState(pricing.clipColorOptions[0]?.code ?? '');
  const [segments, setSegments] = useState<Record<string, Segment[]>>(() =>
    initialSegments(pricing),
  );
  const [engineering, setEngineering] = useState<EngineeringCounts>({});
  const [adjustRows, setAdjustRows] = useState<Record<string, AdjustRow[]>>({});
  const [travelMiles, setTravelMiles] = useState('');
  const [seasonLabel, setSeasonLabel] = useState(`${new Date().getFullYear()} Season`);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [propertyAddress, setPropertyAddress] = useState('');
  const [propertyCity, setPropertyCity] = useState('');
  const [propertyPostal, setPropertyPostal] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  const input: BidInput = useMemo(() => {
    const measurements: Measurement[] = [];
    for (const surface of pricing.surfaces) {
      const parsed = parseSegments(segments[surface.code] ?? [], surface.isAccent);
      if (parsed.length === 0) continue;
      const total = sumOf(parsed);

      measurements.push(
        surface.isAccent
          ? {
              surfaceCode: surface.code as SurfaceCode,
              segments: parsed,
              // How many accent groups were entered — three bushes typed
              // separately rather than one line of six strands.
              unitCount: parsed.length,
              strandCount: total,
            }
          : { surfaceCode: surface.code as SurfaceCode, segments: parsed, linearFeet: total },
      );
    }

    const adjustments: Adjustment[] = [];
    for (const { pkg } of tiers) {
      for (const row of adjustRows[pkg.code] ?? []) {
        const amount = Number(row.dollars);
        if (!row.label.trim() || !row.dollars.trim() || !Number.isFinite(amount) || amount === 0) {
          continue;
        }
        adjustments.push({
          packageCode: pkg.code,
          label: row.label.trim(),
          amountCents: Math.round(amount * 100),
        });
      }
    }

    const engineeringCounts: EngineeringCount[] = [];
    for (const { pkg } of tiers) {
      for (const item of engItems) {
        const qty = Number(engineering[pkg.code]?.[item.sku] ?? '');
        if (!Number.isFinite(qty) || qty <= 0) continue;
        engineeringCounts.push({ packageCode: pkg.code, sku: item.sku, qty: Math.round(qty) });
      }
    }

    return {
      colorCode,
      clipColorCode,
      measurements,
      travelMiles: Number(travelMiles) || 0,
      seasonLabel,
      ...(adjustments.length > 0 ? { adjustments } : {}),
      ...(engineeringCounts.length > 0 ? { engineering: engineeringCounts } : {}),
    };
  }, [
    pricing.surfaces, tiers, engItems, segments, engineering, adjustRows,
    colorCode, clipColorCode, travelMiles, seasonLabel,
  ]);

  /**
   * Priced on every keystroke by the same pure engine the server runs. No
   * network involved, which is exactly why this works with no signal.
   */
  const { result, issues } = useMemo(() => {
    try {
      return { result: computeBid(pricing, input), issues: [] as string[] };
    } catch (err) {
      if (err instanceof BidValidationError) return { result: null, issues: err.issues };
      return { result: null, issues: [(err as Error).message] };
    }
  }, [pricing, input]);

  /** Any edit invalidates the "saved" note, so it never describes a stale bid. */
  function touched(apply: () => void) {
    setSavedNote(null);
    apply();
  }

  async function handleSave() {
    if (!result) return;
    setSaving(true);
    try {
      const blank = (v: string) => (v.trim() === '' ? null : v.trim());
      await onSave(input, result, {
        customer: {
          name: customerName.trim(),
          email: blank(customerEmail),
          phone: blank(customerPhone),
        },
        property: {
          addressLine1: propertyAddress.trim(),
          city: blank(propertyCity),
          region: null,
          postalCode: blank(propertyPostal),
        },
      });

      setSavedNote(
        `Saved — ${result.quotes
          .map((q) => `${q.packageCode} ${formatCents(q.totalCents)}`)
          .join(' · ')}`,
      );
      setSegments(initialSegments(pricing));
      setEngineering({});
      setAdjustRows({});
      setCustomerName('');
      setCustomerEmail('');
      setCustomerPhone('');
      setPropertyAddress('');
      setPropertyCity('');
      setPropertyPostal('');
      setTravelMiles('');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <section className="card">
        <h2>Colours</h2>
        <div className="field">
          <label>Light colour</label>
          <div className="seg">
            {pricing.colorOptions.map((c) => (
              <button
                key={c.code}
                type="button"
                aria-pressed={colorCode === c.code}
                onClick={() => touched(() => setColorCode(c.code))}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label>Clip colour — match the trim or gutter, not the lights</label>
          <div className="seg">
            {pricing.clipColorOptions.map((c) => (
              <button
                key={c.code}
                type="button"
                aria-pressed={clipColorCode === c.code}
                onClick={() => touched(() => setClipColorCode(c.code))}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <Measurements
        pricing={pricing}
        tiers={tiers}
        segments={segments}
        onChange={(surfaceCode, next) =>
          touched(() => setSegments((prev) => ({ ...prev, [surfaceCode]: next })))
        }
      />

      <Engineering
        tiers={tiers}
        items={engItems}
        counts={engineering}
        onChange={(packageCode, sku, value) =>
          touched(() =>
            setEngineering((prev) => ({
              ...prev,
              [packageCode]: { ...prev[packageCode], [sku]: value },
            })),
          )
        }
      />

      <section className="card">
        <h2>Job details</h2>
        <div className="field">
          <label>Customer name</label>
          <input
            value={customerName}
            autoComplete="off"
            onChange={(e) => setCustomerName(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Customer email — needed to send them the bid</label>
          <input
            type="email"
            inputMode="email"
            autoComplete="off"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Customer phone</label>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="off"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Property address</label>
          <input value={propertyAddress} onChange={(e) => setPropertyAddress(e.target.value)} />
        </div>
        <div className="field">
          <label>City</label>
          <input value={propertyCity} onChange={(e) => setPropertyCity(e.target.value)} />
        </div>
        <div className="field">
          <label>Postal code</label>
          <input
            inputMode="numeric"
            value={propertyPostal}
            onChange={(e) => setPropertyPostal(e.target.value)}
          />
        </div>
        <div className="field">
          <label>
            Travel miles — the same charge is folded into all three prices
            {pricing.travelRateCentsPerMile === 0
              ? ', and no travel rate is configured yet, so this adds nothing'
              : `, at ${formatCents(pricing.travelRateCentsPerMile)}/mi`}
          </label>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="0.1"
            value={travelMiles}
            onChange={(e) => touched(() => setTravelMiles(e.target.value))}
          />
        </div>
        <div className="field">
          <label>Season</label>
          <input
            value={seasonLabel}
            onChange={(e) => touched(() => setSeasonLabel(e.target.value))}
          />
        </div>
      </section>

      <Adjustments
        tiers={tiers}
        rows={adjustRows}
        result={result}
        onChange={(packageCode, next) =>
          touched(() => setAdjustRows((prev) => ({ ...prev, [packageCode]: next })))
        }
      />

      {issues.length > 0 && input.measurements.length > 0 && (
        <div className="note bad">
          Cannot price this bid yet:
          <ul>
            {issues.map((i) => (
              <li key={i}>{i}</li>
            ))}
          </ul>
        </div>
      )}

      {result && <Quotes result={result} tiers={tiers} isAdmin={user.role === 'admin'} />}
      {savedNote && <div className="note">{savedNote}</div>}

      <div className="totalbar">
        <div className="inner">
          {result ? (
            <TotalBarFigures quotes={result.quotes} tiers={tiers} />
          ) : (
            <span className="amount">
              <span className="cap">no measurements</span>—
            </span>
          )}
          <button className="primary" disabled={!result || saving} onClick={handleSave}>
            {saving ? 'Saving…' : 'Save bid'}
          </button>
        </div>
      </div>
    </>
  );
}
